/* useTelegramConnect — opens a one-time t.me link to our bot and waits for the bot to
   confirm (CONTEXT.md → Telegram connection). Shared by the blocking connect screen
   and the Reconnect button in Settings. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type TelegramConnection } from '../../api/client';

const POLL_MS = 3000;

export type TelegramConnectState = 'idle' | 'opening' | 'waiting' | 'expired' | 'error';

export function useTelegramConnect(onConnected: (connection: TelegramConnection) => void) {
  const [state, setState] = useState<TelegramConnectState>('idle');
  const [url, setUrl] = useState<string | null>(null);
  const timer = useRef<number | null>(null);
  const done = useRef(onConnected);
  done.current = onConnected;

  const stop = useCallback(() => {
    if (timer.current !== null) window.clearInterval(timer.current);
    timer.current = null;
  }, []);

  useEffect(() => stop, [stop]);

  const start = useCallback(async () => {
    stop();
    // Open the tab inside the click so pop-up blockers allow it; point it at the link once issued.
    const tab = window.open('', '_blank');
    setState('opening');
    try {
      const before = await api.getTelegramConnection();
      const link = await api.createTelegramLink();
      setUrl(link.url);
      if (tab) tab.location.href = link.url;
      else window.location.href = link.url;
      setState('waiting');
      const expiresAt = new Date(link.expires_at).getTime();
      timer.current = window.setInterval(async () => {
        if (Date.now() > expiresAt) { stop(); setState('expired'); return; }
        try {
          const now = await api.getTelegramConnection();
          // A reconnect counts only once the stamp moves past the old connection.
          if (now.connected && now.connected_at !== before.connected_at) {
            stop();
            setState('idle');
            done.current(now);
          }
        } catch { /* transient; keep polling until the link expires */ }
      }, POLL_MS);
    } catch {
      tab?.close();
      setState('error');
    }
  }, [stop]);

  return { state, url, start };
}
